import { useContext, useMemo, useRef, useEffect } from 'react';
import DeleteFilter from '../../assets/delete-filter-icon.svg?react';
import { ViewContext, CatalogDispatchContext } from '../../store/CatalogContext';

interface AppliedFiltersProps {
  group: 'purchased' | 'related' | 'fullCatalog';
}

export default function AppliedFilters({ group }: AppliedFiltersProps) {
  const viewState = useContext(ViewContext);
  const catalogDispatch = useContext(CatalogDispatchContext);

  const { filters, sort } = viewState[group];
  const { searchQuery, collection, category, subCategory, individualBundle } = filters;

  // Keep a ref to always read the freshest view state inside event handlers.
  const viewStateRef = useRef(viewState);
  useEffect(() => {
    viewStateRef.current = viewState;
  }, [viewState]);

  const isFiltered = Boolean(
    searchQuery || collection?.length || category?.length || subCategory?.length || individualBundle?.length
  );

  const persistView = (updatedView: object) => {
    sessionStorage.setItem(
      'catalogView',
      JSON.stringify({
        ...viewState,
        ...updatedView,
      })
    );
  };

  const handleRemoveFilter = (e: React.MouseEvent<HTMLButtonElement>, filterName?: string): void => {
    const target = e.currentTarget;
    const name = target.name;

    const groupView = viewStateRef.current[group];
    const groupFilters = groupView.filters;
    const updatedFilters = { ...groupFilters };

    switch (name) {
      case 'search':
        updatedFilters.searchQuery = null;
        break;
      case 'collection':
        updatedFilters.collection = groupFilters.collection?.filter((c) => c !== filterName) || null;
        break;
      case 'category':
        updatedFilters.category = groupFilters.category?.filter((c) => c !== filterName) || null;
        break;
      case 'subCategory':
        updatedFilters.subCategory = groupFilters.subCategory?.filter((c) => c !== filterName) || null;
        break;
      case 'individualBundle':
        updatedFilters.individualBundle = groupFilters.individualBundle?.filter((b) => b !== filterName) || null;
        break;
      default:
        break;
    }

    // Preserve a cleared search (avoid resurrection if original outer destructure was stale)
    // if (latestFilters.searchQuery === null) {
    //   updatedFilters.searchQuery = null;
    // }

    const updatedView = {
      [group]: {
        ...groupView,
        filters: updatedFilters,
      },
    };

    catalogDispatch({ type: 'VIEW', payload: updatedView });
    persistView(updatedView);
  };

  const handleRemoveAllFilters = () => {
    const updatedView = {
      [group]: {
        ...viewState[group],
        filters: {
          searchQuery: null,
          collection: [],
          category: [],
          subCategory: [],
          individualBundle: [],
        },
        // maintain existing sort except for fullCatalog which defaults to a-z
        sort: group === 'fullCatalog' ? 'a-z' : sort,
      },
    };

    catalogDispatch({
      type: 'VIEW',
      payload: updatedView,
    });

    persistView(updatedView);
  };

  const renderFilterBadges = (
    items: string[] | null | undefined,
    name: 'collection' | 'category' | 'subCategory' | 'individualBundle'
  ) => {
    if (!items || !items.length) return null;

    return items.map((filter) => (
      <span
        key={`${name}:${filter}`}
        className="badge d-inline-flex align-items-baseline bg-secondary-subtle text-reset rounded-pill pe-0 py-0 me-1"
      >
        {filter}
        <button
          className="remove-filter-btn"
          type="button"
          name={name}
          aria-label={`delete ${name} filter`}
          onClick={(e) => handleRemoveFilter(e, String(filter))}
        >
          <DeleteFilter />
        </button>
      </span>
    ));
  };

  const collectionFilters = useMemo(() => renderFilterBadges(collection, 'collection'), [collection]);
  const categoryFilters = useMemo(() => renderFilterBadges(category, 'category'), [category]);
  const subCategoryFilters = useMemo(() => renderFilterBadges(subCategory, 'subCategory'), [subCategory]);
  const individualBundleFilters = useMemo(
    () => renderFilterBadges(individualBundle, 'individualBundle'),
    [individualBundle]
  );

  if (!isFiltered) return null;

  return (
    <div className="col-12 applied-filters mb-4">
      <span className="small fw-500">Filtered by:</span>{' '}
      {searchQuery && (
        <span className="badge d-inline-flex align-items-baseline bg-secondary-subtle text-reset rounded-pill pe-0 py-0 me-1">
          Search: {searchQuery}
          <button
            className="remove-filter-btn"
            type="button"
            name="search"
            aria-label="delete filter"
            onClick={(e) => handleRemoveFilter(e, searchQuery)}
          >
            <DeleteFilter />
          </button>
        </span>
      )}
      {collectionFilters && collectionFilters}
      {categoryFilters && categoryFilters}
      {subCategoryFilters && subCategoryFilters}
      {individualBundleFilters && individualBundleFilters}
      <button
        className="clear-all-filter-btn btn btn-link btn-sm text-reset p-0 ms-2"
        type="button"
        onClick={handleRemoveAllFilters}
      >
        Clear All
      </button>
    </div>
  );
}
